import { appConfirm } from "@/lib/appDialog";
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import MobileFilterRail from "@/components/MobileFilterRail";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Loader2, Power, Users, Plus, Trash2, ShieldCheck, X, UserCheck, Pencil, Info, Save } from "lucide-react";

const ROLES_ORDER = ["student", "association", "mentor", "recreation_mentor", "content_creator", "donor", "recruiter", "faculty", "super_admin"];

export default function UsersAdmin() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [roleCounts, setRoleCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [mentors, setMentors] = useState([]);
  const [mentorId, setMentorId] = useState("");
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [roleEditFor, setRoleEditFor] = useState(null); // user object
  const [supervisorFor, setSupervisorFor] = useState(null); // student user object
  const [detailFor, setDetailFor] = useState(null); // full user details modal
  const [editFor, setEditFor] = useState(null); // edit user modal

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      if (q.trim()) params.set("q", q.trim());
      if (mentorId) params.set("user_id", mentorId);
      const url = params.toString() ? `/users?${params.toString()}` : "/users";
      const { data } = await api.get(url);
      setUsers(data.users || []);
      setRoleCounts(data.role_counts || {});
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => { load(); }, q ? 300 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [role, q, mentorId]);

  useEffect(() => {
    api.get("/directory?role=mentor")
      .then(({ data }) => setMentors(data.users || []))
      .catch(() => setMentors([]));
  }, []);

  const toggle = async (id) => {
    try {
      await api.patch(`/users/${id}/toggle-active`);
      toast.success("تم التحديث");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeUser = async (u) => {
    if (!await appConfirm(`هل تريد فعلاً حذف حساب «${u.name}» (${u.email})؟ لا يمكن التراجع.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("تم حذف الحساب");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="users-admin">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">المستخدمون</div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-heading font-extrabold text-slate-900">إدارة المستخدمين</h1>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center justify-center gap-2 min-h-11 px-5 rounded-xl bg-teal-700 text-white font-bold hover:bg-teal-600 transition-colors w-full sm:w-auto"
            data-testid="new-user-btn"
          >
            <Plus className="h-5 w-5" /> مستخدم جديد
          </button>
        </div>

        <MobileFilterRail
          value={role}
          onChange={(id) => { setRole(id); if (id !== "mentor") setMentorId(""); }}
          filters={useMemo(() => ([
            { id: "", label: "الكل", count: roleCounts.total ?? 0 },
            ...Object.entries(ROLE_LABELS).map(([k, v]) => ({ id: k, label: v, count: roleCounts[k] ?? 0 })),
          ]), [roleCounts])}
        />

        <div className="mb-4 grid gap-3 sm:max-w-3xl sm:grid-cols-2">
          <label className="text-sm font-bold text-slate-700">
            الموجّه
            <select
              value={mentorId}
              onChange={(e) => { setMentorId(e.target.value); if (e.target.value) setRole("mentor"); }}
              className="mt-1.5 min-h-11 w-full rounded-xl border-2 border-slate-200 bg-white px-3 text-sm outline-none transition-all focus:border-teal-600 focus:ring-4 focus:ring-teal-600/10"
              data-testid="users-admin-mentor-filter"
            >
              <option value="">كل الموجّهين</option>
              {mentors.map((mentor) => <option key={mentor.id} value={mentor.id}>{mentor.name}</option>)}
            </select>
          </label>
          <label className="text-sm font-bold text-slate-700">
            بحث عام
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="الاسم أو البريد أو الجهة…"
              className="mt-1.5 min-h-11 w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-sm outline-none transition-all focus:border-teal-600 focus:ring-4 focus:ring-teal-600/10"
              data-testid="users-admin-search"
            />
          </label>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="users-admin-error" />
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="لا يوجد مستخدمون في هذا الفلتر"
            description={role ? "جرّب اختيار دور آخر أو مسح الفلتر." : "لم يتم تسجيل أي مستخدم بعد."}
            testid="users-admin-empty"
          />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {/* Mobile cards (sm and below) */}
            <ul className="md:hidden divide-y divide-slate-100" data-testid="users-mobile-list">
              {users.map((u) => {
                const isSuper = u.role === "super_admin";
                const isSelf = current && current.id === u.id;
                return (
                  <li key={u.id} className="p-4" data-testid={`user-card-${u.id}`}>
                    <button
                      type="button"
                      onClick={() => setDetailFor(u)}
                      className="text-start block w-full"
                      data-testid={`open-user-mobile-${u.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 truncate">{u.name}</div>
                          {u.organization_name && u.organization_name !== u.name && (
                            <div className="text-xs text-slate-500 truncate">{u.organization_name}</div>
                          )}
                          <div className="mt-0.5 text-xs text-slate-400 truncate" dir="ltr">{u.email}</div>
                        </div>
                        <span className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${isSuper ? "bg-amber-100 text-amber-800" : "bg-teal-50 text-teal-800"}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${u.is_active ? "bg-emerald-500" : "bg-red-500"}`} /> {u.is_active ? "نشط" : "معطّل"}
                        </span>
                        {u.city && <span className="text-slate-500">· {u.city}</span>}
                        {u.role === "mentor" && (u.assigned_students_count ?? 0) > 0 && (
                          <span className="text-slate-500">· {u.assigned_students_count} طالب</span>
                        )}
                      </div>
                      {u.role === "mentor" && Array.isArray(u.assigned_association_names) && u.assigned_association_names.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1" data-testid={`mentor-assoc-mobile-${u.id}`}>
                          {u.assigned_association_names.map((n) => (
                            <span key={n} className="rounded-full bg-teal-50 text-teal-800 px-2 py-0.5 text-[10px]">{n}</span>
                          ))}
                        </div>
                      )}
                    </button>
                    {!(isSuper && isSelf) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => setEditFor(u)}
                          className="inline-flex min-h-11 items-center gap-1 px-3 rounded-xl text-xs font-bold bg-amber-50 text-amber-800 active:bg-amber-100"
                          data-testid={`edit-user-mobile-${u.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" /> تعديل
                        </button>
                        <button
                          onClick={() => setRoleEditFor(u)}
                          className="inline-flex min-h-11 items-center gap-1 px-3 rounded-xl text-xs font-bold bg-teal-50 text-teal-700 active:bg-teal-100"
                          data-testid={`edit-role-mobile-${u.id}`}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" /> الصلاحية
                        </button>
                        <button
                          onClick={() => toggle(u.id)}
                          className="inline-flex min-h-11 items-center gap-1 px-3 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 active:bg-slate-200"
                          data-testid={`toggle-user-mobile-${u.id}`}
                        >
                          <Power className="h-3.5 w-3.5" /> {u.is_active ? "تعطيل" : "تفعيل"}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الاسم</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">البريد</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الدور</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الجمعيات والطلاب</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">المدينة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الحالة</th>
                    <th className="text-start px-4 py-3 font-semibold text-slate-700">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isSuper = u.role === "super_admin";
                    const isSelf = current && current.id === u.id;
                    return (
                      <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          <button
                            type="button"
                            onClick={() => setDetailFor(u)}
                            className="text-start hover:text-teal-800 hover:underline decoration-dotted"
                            data-testid={`open-user-${u.id}`}
                            title="عرض كامل البيانات"
                          >
                            {u.name}
                            {u.organization_name && u.organization_name !== u.name && (
                              <span className="text-slate-500 font-normal"> ({u.organization_name})</span>
                            )}
                          </button>
                          {u.role === "mentor" && Array.isArray(u.assigned_association_names) && u.assigned_association_names.length > 0 && (
                            <div className="mt-1 text-xs text-teal-700 font-normal flex flex-wrap gap-1" data-testid={`mentor-assoc-${u.id}`}>
                              <span className="text-slate-400">الجمعية:</span>
                              {u.assigned_association_names.map((n) => (
                                <span key={n} className="rounded-full bg-teal-50 px-2 py-0.5">{n}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600" dir="ltr">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${isSuper ? "bg-amber-50 text-amber-700" : "bg-teal-50 text-teal-700"}`}>
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {u.role === "mentor" ? (
                            <div className="space-y-1.5" data-testid={`mentor-allocations-${u.id}`}>
                              <div className="font-bold text-slate-900">{u.assigned_students_count || 0} طالب</div>
                              {(u.assigned_associations || []).length > 0 ? u.assigned_associations.map((association) => (
                                <div key={association.id} className="text-xs text-teal-800">
                                  {association.name} · {association.student_count} طالب
                                </div>
                              )) : <div className="text-xs text-slate-500">لا توجد جمعية مسكّنة</div>}
                            </div>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.city || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${u.is_active ? "bg-emerald-500" : "bg-red-500"}`} />
                            {u.is_active ? "نشط" : "معطّل"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isSuper && isSelf ? (
                            <span className="text-xs text-slate-400">حسابك الحالي</span>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => setDetailFor(u)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                                data-testid={`view-user-${u.id}`}
                                title="عرض جميع البيانات"
                              >
                                <Info className="h-3.5 w-3.5" /> عرض
                              </button>
                              <button
                                onClick={() => setEditFor(u)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100"
                                data-testid={`edit-user-${u.id}`}
                                title="تعديل البيانات"
                              >
                                <Pencil className="h-3.5 w-3.5" /> تعديل
                              </button>
                              <button
                                onClick={() => setRoleEditFor(u)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-50 text-teal-700 hover:bg-teal-100"
                                data-testid={`edit-role-${u.id}`}
                              >
                                <ShieldCheck className="h-3.5 w-3.5" /> الصلاحية
                              </button>
                              {u.role === "student" && (
                                <button
                                  onClick={() => setSupervisorFor(u)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-50 text-sky-700 hover:bg-sky-100"
                                  data-testid={`assign-supervisor-${u.id}`}
                                  title="إسناد مشرف أكاديمي"
                                >
                                  <UserCheck className="h-3.5 w-3.5" /> مشرف
                                </button>
                              )}
                              <button
                                onClick={() => toggle(u.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                                data-testid={`toggle-user-${u.id}`}
                              >
                                <Power className="h-3.5 w-3.5" />
                                {u.is_active ? "تعطيل" : "تفعيل"}
                              </button>
                              {!isSelf && (
                                <button
                                  onClick={() => removeUser(u)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> حذف
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}
      {roleEditFor && (
        <RoleEditModal
          user={roleEditFor}
          onClose={() => setRoleEditFor(null)}
          onSaved={() => { setRoleEditFor(null); load(); }}
        />
      )}
      {supervisorFor && (
        <SupervisorModal
          student={supervisorFor}
          onClose={() => setSupervisorFor(null)}
          onSaved={() => { setSupervisorFor(null); load(); }}
        />
      )}
      {detailFor && (
        <UserDetailModal
          user={detailFor}
          onClose={() => setDetailFor(null)}
          onEdit={() => { setEditFor(detailFor); setDetailFor(null); }}
        />
      )}
      {editFor && (
        <EditUserModal
          user={editFor}
          onClose={() => setEditFor(null)}
          onSaved={() => { setEditFor(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function UserDetailModal({ user, onClose, onEdit }) {
  const rows = [
    ["الاسم الكامل", user.name],
    ["البريد الإلكتروني", user.email, "ltr"],
    ["الدور", ROLE_LABELS[user.role] || user.role],
    ["اسم الجهة", user.organization_name],
    ["التخصص", user.specialization],
    ["رقم الجوال", user.phone, "ltr"],
    ["الجنس", user.gender === "male" ? "ذكر" : user.gender === "female" ? "أنثى" : user.gender],
    ["المدينة", user.city],
    ["الجامعة", user.university],
    ["الكلية", user.college],
    ["التخصص الأكاديمي", user.major || user.academic_field],
    ["المعدل التراكمي", user.gpa],
    ["خريج؟", user.is_graduate ? "نعم" : "لا"],
    ["نبذة", user.bio],
    ["نشط؟", user.is_active ? "نعم" : "لا"],
    ["تاريخ الإنشاء", user.created_at ? new Date(user.created_at).toLocaleString("ar-SA") : null],
  ].filter(([, v]) => v);
  return (
    <ModalShell title={`بيانات ${user.name}`} onClose={onClose} testid="user-detail-modal">
      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {rows.map(([label, value, dir]) => (
            <div key={label} className="text-sm">
              <div className="text-xs text-slate-500 mb-0.5">{label}</div>
              <div className="text-slate-900 font-medium break-words" dir={dir}>{value}</div>
            </div>
          ))}
        </div>
        {user.skills?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-slate-500 mb-1">المهارات</div>
            <div className="flex flex-wrap gap-1.5">
              {user.skills.map((s) => (
                <span key={s} className="px-2 py-0.5 rounded-full text-xs bg-teal-50 text-teal-800 border border-teal-100">{s}</span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إغلاق</button>
          <button onClick={onEdit} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="detail-open-edit">
            <Pencil className="h-4 w-4" /> تعديل البيانات
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    city: user.city || "",
    organization_name: user.organization_name || "",
    specialization: user.specialization || "",
    university: user.university || "",
    college: user.college || "",
    major: user.major || "",
    academic_field: user.academic_field || "",
    gender: user.gender || "",
    gpa: user.gpa || "",
    bio: user.bio || "",
    is_active: user.is_active !== false,
    is_graduate: !!user.is_graduate,
    linked_mentor_id: user.linked_mentor_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setErr("");
    if (!form.name?.trim() || !form.email?.trim()) { setErr("الاسم والبريد مطلوبان"); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        payload[k] = v;
      });
      payload.is_active = form.is_active;
      payload.is_graduate = form.is_graduate;
      await api.patch(`/users/${user.id}`, payload);
      toast.success("تم تحديث البيانات");
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={`تعديل بيانات ${user.name}`} onClose={onClose} testid="edit-user-modal">
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="الاسم" value={form.name} onChange={(v) => upd("name", v)} testid="eu-name" required />
          <Field label="البريد الإلكتروني" type="email" dir="ltr" value={form.email} onChange={(v) => upd("email", v)} testid="eu-email" required />
          <Field label="رقم الجوال" dir="ltr" value={form.phone} onChange={(v) => upd("phone", v)} testid="eu-phone" />
          <Field label="المدينة" value={form.city} onChange={(v) => upd("city", v)} testid="eu-city" />
          <Field label="اسم الجهة" value={form.organization_name} onChange={(v) => upd("organization_name", v)} testid="eu-org" />
          <Field label="التخصص" value={form.specialization} onChange={(v) => upd("specialization", v)} testid="eu-spec" />
          {user.role === "recreation_mentor" && <MentorLinkField value={form.linked_mentor_id} onChange={(v) => upd("linked_mentor_id", v)} />}
          <Field label="الجامعة" value={form.university} onChange={(v) => upd("university", v)} testid="eu-university" />
          <Field label="الكلية" value={form.college} onChange={(v) => upd("college", v)} testid="eu-college" />
          <Field label="التخصص الأكاديمي" value={form.major} onChange={(v) => upd("major", v)} testid="eu-major" />
          <Field label="المعدل التراكمي" value={form.gpa} onChange={(v) => upd("gpa", v)} testid="eu-gpa" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">الجنس</label>
            <select value={form.gender} onChange={(e) => upd("gender", e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900" data-testid="eu-gender">
              <option value="">— بدون —</option>
              <option value="male">ذكر</option>
              <option value="female">أنثى</option>
            </select>
          </div>
          <div className="flex items-end gap-6 pb-1">
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="eu-is-active-label">
              <input type="checkbox" checked={form.is_active} onChange={(e) => upd("is_active", e.target.checked)} className="rounded" data-testid="eu-is-active" />
              <span className="text-sm text-slate-700">حساب نشط</span>
            </label>
            {user.role === "student" && (
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_graduate} onChange={(e) => upd("is_graduate", e.target.checked)} className="rounded" data-testid="eu-is-graduate" />
                <span className="text-sm text-slate-700">خريج</span>
              </label>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">نبذة</label>
          <textarea rows={3} value={form.bio} onChange={(e) => upd("bio", e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900" data-testid="eu-bio" />
        </div>
        {err && <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="eu-error">{err}</div>}
      </div>
      <div className="p-4 pt-3 border-t border-slate-100 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="eu-cancel">إلغاء</button>
        <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="eu-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ التعديلات
        </button>
      </div>
    </ModalShell>
  );
}

function SupervisorModal({ student, onClose, onSaved }) {
  const [faculty, setFaculty] = useState([]);
  const [selected, setSelected] = useState(student.supervisor_id || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const params = student.university ? `?university=${encodeURIComponent(student.university)}` : "";
        const { data } = await api.get(`/faculty/directory${params}`);
        setFaculty(data.faculty || []);
      } catch (e) { setErr(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [student]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.patch(`/admin/students/${student.id}/supervisor`, { faculty_id: selected || null });
      toast.success(selected ? "تم إسناد المشرف" : "تمت إزالة المشرف");
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`مشرف الطالب: ${student.name}`} onClose={onClose} testid="assign-supervisor-modal">
      <div className="p-4 space-y-4">
        <div className="text-sm text-slate-600">
          الجامعة: <span className="font-semibold text-slate-900">{student.university || "غير محدّدة"}</span>
        </div>
        {loading ? (
          <div className="grid place-items-center py-8"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">اختر عضو هيئة التدريس</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
              data-testid="assign-supervisor-select"
            >
              <option value="">— إزالة المشرف —</option>
              {faculty.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}{f.college ? ` — ${f.college}` : ""}{f.university ? ` (${f.university})` : ""}
                </option>
              ))}
            </select>
            {faculty.length === 0 && (
              <div className="mt-2 text-xs text-amber-700">لا يوجد أعضاء تدريس متوفرون{student.university ? " لهذه الجامعة" : ""}.</div>
            )}
          </div>
        )}
        {err && <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="assign-supervisor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
            حفظ الإسناد
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function MentorLinkField({ value, onChange }) {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { let active = true; api.get("/users?role=mentor").then(({data}) => { if (active) setMentors(data.users || []); }).catch((e) => toast.error(formatApiError(e))).finally(() => { if (active) setLoading(false); }); return () => { active = false; }; }, []);
  return <div><label className="mb-2 block text-sm font-medium text-slate-700">الموجّه الأكاديمي المرتبط</label><select value={value} onChange={(e)=>onChange(e.target.value)} disabled={loading} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900" data-testid="eu-linked-mentor"><option value="">— اختر الموجّه —</option>{mentors.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}</select></div>;
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "student",
    phone: "", city: "", organization_name: "", specialization: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const orgRequired = ["association", "donor", "recruiter", "faculty"].includes(form.role);
  const specRequired = ["mentor", "content_creator", "faculty"].includes(form.role);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.password) { setErr("الاسم والبريد وكلمة السر مطلوبة"); return; }
    if (form.password.length < 6) { setErr("كلمة السر يجب أن تكون 6 أحرف فأكثر"); return; }
    if (orgRequired && !form.organization_name) { setErr("اسم الجهة مطلوب لهذا الدور"); return; }
    if (specRequired && !form.specialization) { setErr("التخصص مطلوب لهذا الدور"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!orgRequired) delete payload.organization_name;
      if (!specRequired) delete payload.specialization;
      await api.post("/users", payload);
      toast.success("تم إنشاء الحساب");
      onCreated();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="إنشاء حساب جديد" onClose={onClose} testid="create-user-modal">
      <form onSubmit={submit} className="p-4 space-y-4" noValidate>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="الاسم" value={form.name} onChange={(v) => update("name", v)} testid="cu-name" required />
          <Field label="البريد الإلكتروني" type="email" dir="ltr" value={form.email} onChange={(v) => update("email", v)} testid="cu-email" required />
          <Field label="كلمة السر" type="text" dir="ltr" value={form.password} onChange={(v) => update("password", v)} testid="cu-password" required placeholder="6 أحرف على الأقل" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">الدور <span className="text-red-500">*</span></label>
            <select
              value={form.role}
              onChange={(e) => update("role", e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
              data-testid="cu-role"
            >
              {ROLES_ORDER.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <Field label="رقم الجوال" dir="ltr" value={form.phone} onChange={(v) => update("phone", v)} testid="cu-phone" />
          <Field label="المدينة" value={form.city} onChange={(v) => update("city", v)} testid="cu-city" />
          {orgRequired && <Field label="اسم الجهة" value={form.organization_name} onChange={(v) => update("organization_name", v)} testid="cu-org" wide required />}
          {specRequired && <Field label="التخصص" value={form.specialization} onChange={(v) => update("specialization", v)} testid="cu-spec" wide required />}
        </div>
        {err && <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="cu-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="cu-cancel">إلغاء</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="cu-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            إنشاء الحساب
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function RoleEditModal({ user, onClose, onSaved }) {
  const [newRole, setNewRole] = useState(user.role);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (newRole === user.role) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}/role`, { role: newRole });
      toast.success(`تم تحديث الدور إلى «${ROLE_LABELS[newRole]}»`);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`تعديل صلاحية «${user.name}»`} onClose={onClose} testid="role-edit-modal">
      <div className="p-4 space-y-5">
        <div className="text-sm text-slate-600" dir="ltr">{user.email}</div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">الصلاحية الجديدة</label>
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
            data-testid="re-role"
          >
            {ROLES_ORDER.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
            يمكن لأي مدير نظام منح هذه الصلاحية أو سحبها من مدير آخر. لا يمكنك تغيير صلاحية حسابك الحالي، ولا إزالة آخر مدير نظام نشط.
          </div>
        </div>
        {err && <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="re-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="re-cancel">إلغاء</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="re-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            حفظ
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid={testid} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-heading font-bold text-slate-900 text-lg">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="إغلاق" data-testid={`${testid}-close`}>
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", dir, wide, required, placeholder }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {label} {required && <span className="text-red-500" aria-hidden="true">*</span>}
      </label>
      <input
        type={type}
        dir={dir}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
        data-testid={testid}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
}
